// LEGACY UI: replace with NativeSelect from @workspace/frontend-ui.
// Don't import this in new code; legacy-ui.json tracks the remaining importers
// and only shrinks (scripts/legacy-ui-check.mjs).

import classNames from "@casino/external/classNames";
import ControlsChevronDownSmall from "@casino/external/icons/ControlsChevronDownSmall";
import { forwardRef } from "react";

const getSizeStyles = (size?: string) => {
  switch (size) {
    case "xl":
      return "h-14 leading-[3.5rem]";
    case "lg":
      return "h-12 leading-[3rem]";
    case "sm":
      return "h-8 leading-8 rounded-sm";
    default:
      return "h-10 leading-10";
  }
};

type Props = {
  className?: string;
  size?: "sm" | "md" | "lg" | "xl";
  error?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  languageSelectTextColor?: string;
  children?: React.ReactNode;
} & Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "size">;

const NativeSelect = forwardRef<HTMLSelectElement, Props>(
  (
    {
      children,
      className,
      size = "md",
      error,
      disabled,
      readOnly,
      languageSelectTextColor = "text-foreground",
      ...rest
    },
    ref,
  ) => {
    const isDisabled = disabled || readOnly;

    return (
      <span
        className={classNames(
          "block relative w-full",
          isDisabled && "opacity-30 cursor-not-allowed",
          readOnly && "cursor-not-allowed",
          className,
        )}
        data-testid="native-select"
      >
        <select
          ref={ref}
          disabled={isDisabled}
          aria-invalid={!!error}
          className={classNames(
            "block w-full max-w-full py-0 px-4 m-0 appearance-none text-base transition-shadow box-border relative z-[2] cursor-pointer",
            languageSelectTextColor,
            "bg-background inset-ring inset-ring-border hover:inset-ring-2 hover:inset-ring-border",
            "focus:inset-ring-2 focus:inset-ring-primary focus:outline-none",
            error &&
              "inset-ring-2 inset-ring-destructive hover:inset-ring-2 hover:inset-ring-destructive focus:inset-ring-2 focus:inset-ring-destructive",
            "invalid:inset-ring-2 invalid:inset-ring-destructive invalid:hover:inset-ring-2 invalid:hover:inset-ring-destructive invalid:focus:inset-ring-2 invalid:focus:inset-ring-destructive",
            "rounded-md",
            getSizeStyles(size),
            "before:box-border after:box-border",
            "placeholder:text-muted-foreground placeholder:opacity-100 placeholder:transition-opacity placeholder:delay-75",
            isDisabled &&
              "cursor-not-allowed hover:inset-ring hover:inset-ring-border",
          )}
          {...rest}
        >
          {children}
        </select>

        <ControlsChevronDownSmall
          className={classNames(
            "absolute top-1/2 end-3 -translate-y-1/2 z-5 pointer-events-none",
            "flex-shrink-0 transition-transform",
            languageSelectTextColor,
            size === "sm" ? "text-base" : "text-xl",
          )}
        />
      </span>
    );
  },
);

export default NativeSelect;
